import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./tokenlyglobal.css";
import "./alert-styles.css";
import { checkAuthAndRedirect } from "./utils";
import CompactTracking from "./CompactTracking";

// 🌐 Default Coordinates for Fallback (e.g., Mountain View, CA)
const DEFAULT_LATITUDE = 37.4219983;
const DEFAULT_LONGITUDE = -122.084;
const DEFAULT_RADIUS = "20000";

const AllDoctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const [allSpecialties, setAllSpecialties] = useState([]);
  const [userLocation, setUserLocation] = useState({
    latitude: DEFAULT_LATITUDE,
    longitude: DEFAULT_LONGITUDE,
    hasFetched: false, // Tracks if location logic has completed
  });
  const navigate = useNavigate();

  // ✅ CORE LOGIC: Determines Doctor Status (Available, Ideal, Not Available)
  const getDoctorStatus = useCallback((doc) => {
    // Status Display Logic (matches mobile app lines 280-286)
    // IMPORTANT: current_token can be 0, which is a valid token number, so check explicitly for null
    if (doc.current_token !== null) {
      return { text: "Available", color: "available", bookable: true };
    } else {
      return { text: "Ideal", color: "idle", bookable: true };
    }
  }, []);

  // ✅ BOOKING LOGIC: Determines if Generate Token button should show (matches mobile app lines 300-320)
  const isBookable = useCallback((doc) => {
    const slots = doc.hours_detail?.time || [];
    const isClosed = doc.hours_detail?.is_closed === 1;
    const hasNoSlots = slots.length === 0;
    const hasEmptyHoursDetail = (!doc.hours_detail) || (Array.isArray(doc.hours_detail) && doc.hours_detail.length === 0);

    // Not Available Today if: Closed, No Slots, or Empty hours_detail
    if (isClosed || hasNoSlots || hasEmptyHoursDetail) {
      return false;
    }

    // Otherwise bookable (regardless of current_token status)
    return true;
  }, []);

  // 1. 🌐 Function to Fetch Doctors (memoized using useCallback)
  const fetchDoctors = useCallback(async (lat, lon) => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch("https://tokenly.biswaas.com/api/doctor-list", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          doctorName: "",
          latitude: lat,
          longitude: lon,
          radius: DEFAULT_RADIUS,
          specialties: [],
          a: "a",
        }),
      });

      const json = await res.json();
      const list = Array.isArray(json.data?.rows) ? json.data.rows : [];
      setDoctors(list);

      // Extract all unique specialties for filter dropdown
      const specialties = new Set();
      list.forEach(doc => {
        if (doc.specialties && doc.specialties !== "null") {
          const specialtiesArr = JSON.parse(doc.specialties);
          specialtiesArr.forEach(spec => specialties.add(spec));
        }
      });
      setAllSpecialties(Array.from(specialties).sort());
    } catch (e) {
      setError(e.message || "Error fetching doctors");
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. 📍 useEffect to Handle Location & Initial Fetch
  useEffect(() => {
    const success = (position) => {
      const { latitude, longitude } = position.coords;
      console.log("Location fetched successfully:", latitude, longitude);
      setUserLocation({ latitude, longitude, hasFetched: true });
      fetchDoctors(latitude, longitude);
    };

    const error = (err) => {
      console.warn(`Geolocation Error(${err.code}): ${err.message}`);

      let errorMessage = "Could not get your location. ";

      if (err.code === 1) {
        errorMessage = "Location permission was denied. ";
      } else if (err.code === 2) {
        errorMessage = "Location unavailable. ";
      } else if (err.code === 3) {
        errorMessage = "Location request timeout. ";
      }

      alert(errorMessage + "Showing doctors for a default location. Please enable location in your device settings.");

      setUserLocation({
        latitude: DEFAULT_LATITUDE,
        longitude: DEFAULT_LONGITUDE,
        hasFetched: true,
      });
      fetchDoctors(DEFAULT_LATITUDE, DEFAULT_LONGITUDE);
    };

    // Request location with high accuracy
    if (navigator.geolocation) {
      console.log("Requesting geolocation...");
      navigator.geolocation.getCurrentPosition(success, error, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
    } else {
      alert("Geolocation is not supported by your browser. Showing doctors for a default location.");
      setUserLocation({
        latitude: DEFAULT_LATITUDE,
        longitude: DEFAULT_LONGITUDE,
        hasFetched: true,
      });
      fetchDoctors(DEFAULT_LATITUDE, DEFAULT_LONGITUDE);
    }
  }, [fetchDoctors]);

  // --- Render Handling for Location/Loading States ---

  // --- Render Handling for Location/Loading States ---

  if (!userLocation.hasFetched || loading) {
    return (
      <section className="bg-light" style={{ minHeight: '100vh', paddingTop: '120px', paddingBottom: '3rem' }}>
        <div className="container-xl">
          <div className="text-center mb-5 pt-4">
            <div className="spinner-grow text-primary mb-3" role="status"></div>
            <h2 className="h3 fw-bold text-dark">Finding Nearby Doctors</h2>
            <p className="text-muted">Tailoring results based on your location...</p>
          </div>
          <div className="row g-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div className="col-md-6 col-lg-4 mb-4" key={i}>
                <div className="doctor-skeleton"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) return (
    <div className="d-flex flex-column justify-content-center align-items-center py-5" style={{ minHeight: '60vh' }}>
      <i className="fas fa-exclamation-circle text-danger mb-3" style={{ fontSize: '3rem' }}></i>
      <h3 className="fw-bold">Something went wrong</h3>
      <p className="text-muted">{error}</p>
      <button className="btn btn-primary mt-3 rounded-pill px-4" onClick={() => fetchDoctors(userLocation.latitude, userLocation.longitude)}>
        Try Again
      </button>
    </div>
  );

  // --- Filtering Logic ---

  const filteredDoctors = doctors.filter((doc) => {
    const name = doc.name_title ? `${doc.name_title} ${doc.fullname}` : doc.fullname;
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());

    // Check specialty filter
    let matchesSpecialty = true;
    if (selectedSpecialty) {
      const specialtiesArr = doc.specialties && doc.specialties !== "null"
        ? JSON.parse(doc.specialties)
        : [];
      matchesSpecialty = specialtiesArr.includes(selectedSpecialty);
    }

    return matchesSearch && matchesSpecialty;
  });

  // --- Component JSX ---

  return (

    <section id="doctors-list" className="bg-light" style={{ minHeight: '100vh', paddingBottom: '2rem' }}>
      {/* Hero Section with Background Image - Same as other pages */}
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container-fluid container-xl px-4 px-md-3">
          <h1 className="display-5 fw-bold text-white mb-1">Available <span className="text-warning">Doctors</span></h1>
          <p className="text-light mb-0">
            Book appointments with top-rated medical professionals nearby
          </p>
        </div>
      </section>
      <CompactTracking />

      <div className="container-xl">
        {userLocation.latitude === DEFAULT_LATITUDE && (
          <div className="row mt-4">
            <div className="col-12 text-center">
              <div className="d-inline-flex align-items-center bg-white p-2 border rounded-pill shadow-sm">
                <span className="small text-muted px-3"><i className="fas fa-map-marker-alt text-warning me-2"></i>Using default location</span>
                <button
                  className="btn btn-primary btn-sm rounded-pill px-3"
                  onClick={() => {
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        const { latitude, longitude } = pos.coords;
                        setUserLocation({ latitude, longitude, hasFetched: true });
                        fetchDoctors(latitude, longitude);
                      },
                      (err) => {
                        alert("Please enable location permissions to find doctors near you.");
                      },
                      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
                    );
                  }}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter Controls */}
        <div className="filter-container mt-4">
          <div className="row g-3">
            <div className="col-md-5">
              <label className="form-label small fw-bold text-uppercase text-muted">Search name</label>
              <div className="search-input-group">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  className="form-control shadow-none"
                  placeholder="e.g. Dr. John Doe..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-bold text-uppercase text-muted">Specialty</label>
              <select
                className="form-select filter-select shadow-none"
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
              >
                <option value="">All Specialties</option>
                {allSpecialties.map((specialty) => (
                  <option key={specialty} value={specialty}>
                    {specialty}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary clear-btn w-100 shadow-sm"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedSpecialty("");
                }}
              >
                <i className="fas fa-undo-alt me-2"></i>
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Doctor Cards */}
        <div className="row g-4">
          {filteredDoctors.length === 0 ? (
            <div className="col-12">
              <div className="empty-state">
                <i className="fas fa-user-md-slash"></i>
                <h3>No doctors found</h3>
                <p>Try adjusting your search or filters to find what you're looking for.</p>
                <button className="btn btn-link text-primary" onClick={() => { setSearchTerm(""); setSelectedSpecialty(""); }}>
                  Clear all filters
                </button>
              </div>
            </div>
          ) : (
            filteredDoctors.map((doc) => {
              const name = doc.name_title ? `${doc.name_title} ${doc.fullname}` : doc.fullname;
              const specialtiesArr =
                doc.specialties && doc.specialties !== "null"
                  ? JSON.parse(doc.specialties)
                  : [];
              const speciality = specialtiesArr[0] || "General Physician";
              const slots = doc.hours_detail?.time || [];
              const photo = doc.profile_photo_full;
              const statusInfo = getDoctorStatus(doc);
              const canBook = isBookable(doc);

              return (
                <div className="col-md-6 col-lg-4" key={doc.id}>
                  <article className={`doctor-card doctor-card--${statusInfo.color}`}>
                    <header className="doctor-card__header">
                      <div className="doctor-card__avatar-wrap">
                        <img
                          src={photo}
                          alt={name}
                          className="doctor-card__avatar"
                          onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/3774/3774299.png" }}
                        />
                      </div>
                      <div className="doctor-card__title">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h3 
                            className="doctor-card__name text-truncate" 
                            style={{ maxWidth: '140px', cursor: 'pointer', color: '#2563eb' }} 
                            title={name}
                            onClick={() => {
                              if (checkAuthAndRedirect(navigate)) {
                                navigate(`/doctor/${doc.id}`);
                              }
                            }}
                          >
                            {name}
                          </h3>
                          <span className={`doctor-card__status doctor-card__status--${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </div>
                        <p className="doctor-card__speciality mb-1">{speciality}</p>
                        <p className="doctor-card__clinic text-truncate small mb-0" style={{ color: '#000000' }}>
                          <i className="fas fa-hospital me-1 text-muted" style={{ color: '#000000' }}></i> {doc.clinic_name}
                        </p>
                      </div>
                    </header>

                    <section className="doctor-card__body">
                      {doc.is_free_token === 1 && (
                        <div className="small text-success mb-2">
                          <i className="fas fa-info-circle me-1"></i> No token charges will apply
                        </div>
                      )}
                      <div className="doctor-card__meta-box" style={{ backgroundColor: '#f9fafb' }}>
                        <span className="doctor-card__meta-label" style={{ color: '#000000' }}><i className="fas fa-map-marker-alt me-1"></i> Clinic Location</span>
                        <div className="text-truncate small" style={{ color: '#000000' }} title={doc.street_address}>{doc.street_address}</div>
                      </div>

                      <div className="doctor-card__time-row">
                        {slots.slice(0, 2).map((slot, i) => (
                          <span className="doctor-card__time-chip" key={i}>
                            <i className="far fa-clock text-primary"></i>
                            {slot.start_time}
                          </span>
                        ))}
                        {slots.length > 2 && (
                          <span className="small text-muted align-self-center">+{slots.length - 2} more</span>
                        )}
                        {slots.length === 0 && <span className="small text-muted italic">No slots today</span>}
                      </div>
                    </section>

                    <footer className="doctor-card__footer">
                      {!canBook ? (
                        <div className="text-danger fw-bold">Not Available Today</div>
                      ) : (
                        <button
                          className="btn view-profile-btn"
                          onClick={() => {
                            if (checkAuthAndRedirect(navigate)) {
                              navigate(`/doctor/${doc.id}`);
                            }
                          }}
                        >
                          Book Token <i className="fas fa-arrow-right ms-2 small"></i>
                        </button>
                      )}
                    </footer>
                  </article>
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default AllDoctors;